import { CircularProgress, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { toast } from "react-toastify";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const PdfView = ({
  file,
  pageScale,
  pageNumberProps,
  setLoadingFalse,
  setTotalPagesProps,
  sendNotificacion,
  paginated,
  progressiveRender = false,
  batchSize = 50,
  maxPages = 200,
}) => {
  const [totalPages, setTotalPages] = useState(0);
  const [renderedPages, setRenderedPages] = useState(0);
  const [isProgressiveRendering, setIsProgressiveRendering] = useState(false);
  const [renderingComplete, setRenderingComplete] = useState(false);

  const progressToastRef = useRef(null);
  const successToastShownRef = useRef(false);

  function onDocumentLoadSuccess({ numPages }) {
    setTotalPages(numPages);
    if (setTotalPagesProps) {
      setTotalPagesProps(numPages);
    }

    // !!!
    const shouldUseProgressiveRender = !paginated && (numPages > maxPages || progressiveRender);
    setIsProgressiveRendering(shouldUseProgressiveRender);
    setRenderingComplete(false);

    // Toast
    if (shouldUseProgressiveRender) {
      setRenderedPages(0);
      successToastShownRef.current = false;
      progressToastRef.current = toast.info(
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', }}>
          <CircularProgress size={20} thickness={4} />
          <span>Cargando documento: {numPages} páginas</span>
        </div>,
        {
          position: "bottom-right",
          autoClose: false,
          closeButton: false,
          progress: 0, // Inicia en 0%
          icon: false,
        }
      );
    } else {
      setRenderedPages(numPages);
      setRenderingComplete(true);
    }
  }

  // Manejo de renderizado progresivo
  const onProgressiveRenderSuccess = useCallback(() => {
    if (renderingComplete || successToastShownRef.current) {
      return;
    }

    let shouldShowSuccessToast = false;

    setRenderedPages(prev => {
      const newCount = prev + batchSize;
      const finalCount = Math.min(newCount, totalPages);

      const isComplete = finalCount >= totalPages;

      if (isComplete && !successToastShownRef.current) {
        setRenderingComplete(true);
        successToastShownRef.current = true;
        shouldShowSuccessToast = true;

        if (progressToastRef.current) {
          toast.dismiss(progressToastRef.current);
          progressToastRef.current = null;
        }
      } else if (!isComplete && progressToastRef.current) {
        const progressValue = finalCount / totalPages;
        toast.update(progressToastRef.current, {
          render: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CircularProgress size={20} thickness={4} />
              <span>Cargando documento: {finalCount}/{totalPages} páginas</span>
            </div>
          ),
          progress: progressValue,
        });
      }

      return finalCount;
    });

    if (shouldShowSuccessToast) {
      setTimeout(() => {
        toast.success(
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>Documento cargado ({totalPages} páginas)</span>
          </div>,
          {
            position: "bottom-right",
            autoClose: 3000,
          }
        );
      }, 100);
    }
  }, [totalPages, batchSize]);

  // Limpiar toast al desmontar el componente
  useEffect(() => {
    return () => {
      if (progressToastRef.current) {
        toast.dismiss(progressToastRef.current);
      }
    };
  }, []);



  const pagesToShow = isProgressiveRendering
    ? Math.min(renderedPages + batchSize, totalPages)
    : totalPages;

  return (
    <Document
      file={`data:application/pdf;base64,${file}`}
      onLoadSuccess={(props) => {
        onDocumentLoadSuccess(props);
        if (setLoadingFalse) {
          setLoadingFalse();
        }
        if (sendNotificacion) {
          sendNotificacion();
        }
      }}
      error={
        <Typography>
          Error al cargar el archivo PDF
        </Typography>
      }
      loading={
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          padding: '60px 40px'
        }}>
          <CircularProgress size={40} thickness={4} />
          <Typography style={{ marginTop: '20px', color: '#6c757d' }}>
            Cargando documento...
          </Typography>
        </div>
      }
    >
      {paginated ?
        <Page
          pageNumber={pageNumberProps || 1}
          scale={pageScale ? pageScale : 1}
        />
        :
        Array.from(new Array(pagesToShow), (el, index) => {
          const currentPageNum = index + 1;
          const isLastPageInCurrentBatch = currentPageNum === pagesToShow;
          const shouldTriggerNext = isProgressiveRendering &&
            isLastPageInCurrentBatch &&
            !renderingComplete;

          return (
            <Page
              key={`page_${currentPageNum}`}
              pageNumber={currentPageNum}
              scale={pageScale ? pageScale : 1}
              onRenderSuccess={shouldTriggerNext ? onProgressiveRenderSuccess : null}
            />
          );
        })
      }
    </Document>
  );
};

export default PdfView;
